import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { LANGUAGE_ENUMS } from '../../constants/staticPageConstants';
import { productReturnPath } from '../../utils/SSOLogin';
import {
  excludedRegexList,
  getCategoryName,
  includedRegexList,
} from '../../utils/getCategory';
import { identifyUserInNetcore } from '../../utils/netcore';
import { handleTrack } from '../../utils/tracker';
import { ButtonVariants } from '../Atoms/Button';
import { ButtonWithAnchor } from '../income-tax/page-sections/hero-section/sticky-button';
import { loginLink, loginLinkViewPlans } from '../income-tax/static/data';
import { MyAccountsDropdown } from './DropdownOptions';
import { DropdownComponent } from './DynamicHeader/Dropdown/DropdownComponent';
import {
  HEADER_LABELS,
  MY_ACCOUNTS,
} from './DynamicHeader/Dropdown/DropdownData';
import {
  OneTabLogin,
  checkLoginStatus,
  setQueryParamToLink,
  userLogout,
} from './utils';

const { publicRuntimeConfig } = getConfig();

interface IProps {
  buttonColor?: ButtonVariants;
  link?: string;
  language?: LANGUAGE_ENUMS;
  showPlanVariant?: 'variantA' | 'variantB';
  btnClasses?: string;
  btnText?: string;
}

const LoginButton = ({
  buttonColor = ButtonVariants.bluefilled,
  language = LANGUAGE_ENUMS.English,
  link = `${publicRuntimeConfig.SSO_ENDPOINT}${productReturnPath['default']}`,
  showPlanVariant,
  btnClasses = '',
  btnText = HEADER_LABELS[language as keyof typeof HEADER_LABELS].login,
}: IProps): JSX.Element => {
  const [isLoggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    checkLoginStatus().then((data) => {
      const userEmail = data?.username;
      if (!data?.isloggedin) {
        OneTabLogin(setLoggedIn);
      } else {
        if (userEmail) {
          localStorage.setItem('userEmail', userEmail);

          setLoggedIn(true);
          identifyUserInNetcore(userEmail);
        }
      }
      if (userEmail) {
        localStorage.setItem('isLoggedIn', 'true');
      }
    });
  }, []);

  const logout = () => {
    userLogout()
      .then(checkLoginStatus)
      .then(() => {
        setLoggedIn(false);
        localStorage.removeItem('userEmail');
        localStorage.removeItem('isLoggedIn');
        if (typeof window.smartech === 'function') {
          window.smartech('identify', ''); // Clear identification in Netcore
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const router = useRouter();
  const pageName = getCategoryName({
    regexList: [...includedRegexList, ...excludedRegexList],
    url: router.asPath,
    parent: true,
  });
  const refValue = `${pageName}_pages_navbar`;

  let btnLink = setQueryParamToLink(link, 'utm_ref', refValue);

  if (showPlanVariant === 'variantA') {
    btnLink = loginLink;
  } else if (showPlanVariant === 'variantB') {
    btnLink = loginLinkViewPlans;
  }

  return (
    <div>
      {isLoggedIn ? (
        <DropdownComponent
          dropdownLabel="My Accounts"
          Dropdown={MyAccountsDropdown}
          dropdownData={MY_ACCOUNTS}
          classes="px-3 sm:p-0 relative sm:px-0 sm:py-0"
          logoutFunction={logout}
          hover={true}
        />
      ) : (
        <div
          className="px-3 sm:px-0 sm:py-0"
          onClick={(e) =>
            handleTrack({
              label: 'navbar-button',
              obj: e.target,
              action: 'Click_login',
            })
          }
        >
          <ButtonWithAnchor
            buttonLink={btnLink}
            trackLabel="navbar_button"
            buttonVariant={buttonColor}
            className={`text-base whitespace-nowrap sm:px-3 sm:py-2 ${btnClasses}`}
            buttonText={btnText}
          />
        </div>
      )}
    </div>
  );
};

export { LoginButton };
